/**
 * Image Lightbox Section — frontend behavior.
 *
 * Each rendered "BSEIT: Image Lightbox Section" block is a visible container
 * (`.bseit-image-lightbox`) wrapping its own InnerBlocks content (typically
 * Custom HTML). This script walks every container and wires the <img> elements
 * INSIDE that container into a FancyBox gallery, so each block instance is its
 * own self-contained lightbox — no IDs or CSS selectors required.
 *
 * The user's HTML may contain plain <img> tags rather than <a> links. FancyBox
 * requires anchors to open the lightbox, so we wrap each eligible <img> in an
 * anchor at runtime. The anchor uses `display: contents` (see style.scss) so it
 * generates no box and the original image layout is preserved exactly.
 *
 * RTL note: FancyBox's internals break on RTL pages (the slide track / content
 * inherit `direction: rtl`, which breaks image positioning and causes flashing
 * / "black screen" during navigation). We deliberately do NOT enable the
 * Carousel's RTL mode. Instead we force FancyBox internals back to LTR on RTL
 * pages — the same proven fix used by the e-library Reports component
 * (see src/components/Reports.component.js).
 */
import { Fancybox } from "@fancyapps/ui";

/**
 * Detects whether the current page is rendered right-to-left.
 *
 * @return {boolean} true when the <html> element has dir="rtl".
 */
const isRTL = () => document.documentElement.dir === "rtl";

/**
 * CSS injected on RTL pages to force FancyBox internals back to LTR.
 *
 * On Arabic (RTL) pages, `direction: rtl` cascades into the FancyBox container
 * and corrupts the carousel track / slide content / thumbnail strip, causing
 * the lightbox to flash, images to misbehave on next/prev, and the thumbnail
 * strip to render off-screen. Forcing these internals to LTR fixes the gallery
 * without affecting the surrounding page. This mirrors the e-library fix in
 * src/components/Reports.component.js (which disables thumbnails, so it omits
 * the `.f-thumbs*` selectors needed here), scoped to our `bseit-lb-fancybox`
 * mainClass so it cannot leak to other FancyBox users.
 */
const RTL_FANCYBOX_STYLES = `
[dir="rtl"] .bseit-lb-fancybox.fancybox__container,
[dir="rtl"] .bseit-lb-fancybox .fancybox__slide,
[dir="rtl"] .bseit-lb-fancybox .fancybox__content,
[dir="rtl"] .bseit-lb-fancybox .fancybox__carousel,
[dir="rtl"] .bseit-lb-fancybox .fancybox__track,
[dir="rtl"] .bseit-lb-fancybox .f-thumbs,
[dir="rtl"] .bseit-lb-fancybox .f-thumbs__viewport,
[dir="rtl"] .bseit-lb-fancybox .f-thumbs__track,
[dir="rtl"] .bseit-lb-fancybox .f-thumbs__slide {
	direction: ltr !important;
}
[dir="rtl"] .bseit-lb-fancybox .fancybox__content iframe {
	left: 0 !important;
	right: auto !important;
}
`;

/**
 * Injects the RTL fix stylesheet once, returning a remover.
 *
 * @return {Function} Removes the injected <style> from <head>.
 */
const injectRtlStyles = () => {
	const styleEl = document.createElement("style");
	styleEl.textContent = RTL_FANCYBOX_STYLES;
	document.head.appendChild(styleEl);
	return () => styleEl.remove();
};

/**
 * A tiny CSS attribute selector escaper so a group id can be safely embedded
 * in a CSS selector for Fancybox.bind().
 *
 * @param {string} group - The data-fancybox group value.
 * @return {string} A CSS-attribute-selector-safe version of the value.
 */
const escapeGroupForSelector = (group) => group.replace(/["\\]/g, "\\$&");

/**
 * Decides whether a given <img> should open in the lightbox.
 *
 * The PRIMARY discriminator is `object-fit`: hand-coded pages use Tailwind
 * `object-cover` (or `object-fill`) for real photo thumbnails, and
 * `object-contain` for decorative icons (sport pictograms, feature icons,
 * etc.). This is reliable regardless of viewport/section layout, unlike a
 * pixel-size threshold (photo thumbnails can render as small as ~82px, which
 * overlaps with icon sizes — a size cutoff either misses thumbnails or lets
 * icons through).
 *
 * We check BOTH the computed style and the className string. The className is
 * the more reliable signal on sites where the Tailwind utility isn't actually
 * generating real `object-fit` CSS (observed returning "fill" for everything),
 * but we keep the computed-style check too for plain `<img style="object-fit:
 * contain">` markup.
 *
 * Secondary guards: explicit opt-out (`data-no-lightbox` / `.no-lightbox`),
 * already-wrapped, and tiny spacer/tracking pixels.
 *
 * @param {HTMLImageElement} img     The image to test.
 * @param {number}          minSize Minimum RENDERED width AND height, in CSS px,
 *                                  used only as a floor against spacer pixels.
 * @return {boolean} true when the image should be wrapped for the lightbox.
 */
const isIconImage = (img) => {
	if (/\bobject-contain\b/.test(img.className)) return true;
	if (window.getComputedStyle(img).objectFit === "contain") return true;
	return false;
};

const shouldWrapImage = (img, minSize) => {
	if (img.dataset.bseitLbWrapped === "1") return false;
	if (img.dataset.noLightbox !== undefined) return false;
	if (img.classList.contains("no-lightbox")) return false;

	// Decorative icons: page authors use `object-contain` for icon-style images.
	if (isIconImage(img)) return false;

	// Secondary floor: skip tiny spacer/tracking pixels that somehow pass above.
	const rect = img.getBoundingClientRect();
	if (rect.width > 0 && rect.height > 0) {
		if (rect.width < minSize || rect.height < minSize) return false;
	} else {
		const nw = img.naturalWidth || 0;
		const nh = img.naturalHeight || 0;
		if (nw > 0 && nh > 0 && (nw < minSize || nh < minSize)) return false;
	}

	return true;
};

/**
 * Wraps all eligible <img> elements within a container root in FancyBox anchors
 * belonging to the given group, then binds FancyBox to those anchors.
 *
 * Scanning is limited to `root` so each block instance forms its own gallery.
 *
 * @param {HTMLElement} root      The `.bseit-image-lightbox` container.
 * @param {string}      group     Unique FancyBox group id for this instance.
 * @param {number}      minSize   Minimum image size threshold in px.
 * @param {boolean}     captions  Whether to show the image alt as a caption.
 * @param {boolean}     loop      Whether prev/next wraps around.
 * @return {Function|null} Cleanup function (unbind/close) or null when nothing was bound.
 */
const enableLightbox = (root, group, minSize, captions, loop) => {
	const images = root.querySelectorAll("img");

	images.forEach((img) => {
		if (!shouldWrapImage(img, minSize)) return;

		const src = img.currentSrc || img.src;
		if (!src) return;

		const anchor = document.createElement("a");
		anchor.className = "bseit-lb-anchor";
		anchor.href = src;
		anchor.setAttribute("data-fancybox", group);

		const alt = img.getAttribute("alt");
		if (captions && alt) {
			anchor.setAttribute("data-caption", alt);
		}

		// Insert the anchor at the image's position, then move the image inside.
		// The image keeps all its own classes/styles; the anchor generates no box
		// thanks to `display: contents`.
		if (img.parentNode) {
			img.parentNode.insertBefore(anchor, img);
			anchor.appendChild(img);
			img.dataset.bseitLbWrapped = "1";
		}
	});

	const selector = `a[data-fancybox="${escapeGroupForSelector(group)}"]`;

	// Only bind if at least one anchor was created for this group.
	if (!document.querySelector(selector)) return null;

	Fancybox.bind(selector, {
		groupAll: true,
		infinite: !!loop,
		// Render several slides ahead/behind so neighbor images are already
		// decoded and ready when the user clicks next/prev. Default is 1, which
		// shows a white flash between large images that haven't loaded yet.
		mainClass: "bseit-lb-fancybox",
		// Disable idle mode. By default FancyBox hides the toolbar + thumbnail
		// strip after a few seconds of inactivity (the `is-idle` state), which
		// makes the close button and thumbs look like they never appear. Setting
		// `idle: false` keeps the whole UI permanently visible.
		idle: false,
		Carousel: {
			// NOTE: deliberately NOT setting `rtl` here. Enabling Carousel RTL
			// corrupts the slide track on RTL pages (the flashing / misbehaving
			// images bug). RTL is handled instead via injected LTR styles — see
			// injectRtlStyles() above.
			preload: 3,
		},
		// Keep the toolbar so the ✕ close button shows, plus prev/next.
		Toolbar: {
			display: {
				left: [],
				right: ["close"],
			},
		},
	});

	// Return a cleanup function mirroring the rest of the plugin's pattern.
	return () => {
		Fancybox.unbind(selector);
		Fancybox.close();
	};
};

/**
 * Initializes all lightbox containers present on the page. Each container is
 * processed independently, so multiple instances each form their own gallery.
 *
 * @return {Function} Combined cleanup for all bound instances.
 */
const initAll = () => {
	const containers = document.querySelectorAll(".bseit-image-lightbox");
	const cleanups = [];

	containers.forEach((root) => {
		const group = root.dataset.group || "";
		const minSize = parseInt(root.dataset.minSize || "150", 10) || 0;
		const showCaptions = root.dataset.showCaptions === "1";
		const loop = root.dataset.loop !== "0";

		if (!group) return;

		const cleanup = enableLightbox(root, group, minSize, showCaptions, loop);
		if (typeof cleanup === "function") cleanups.push(cleanup);
	});

	return () => cleanups.forEach((fn) => fn());
};

// Run after the DOM is parsed so all containers and images exist. A classic
// WordPress page loads once (no SPA navigation), so a single pass suffices.
const bootstrap = () => {
	// On RTL pages, force FancyBox internals back to LTR (see injectRtlStyles).
	const removeRtlStyles = isRTL() ? injectRtlStyles() : () => {};

	const teardown = initAll();

	// Best-effort cleanup on page unload to mirror the plugin's bind/unbind habit.
	window.addEventListener(
		"pagehide",
		() => {
			teardown();
			removeRtlStyles();
		},
		{ once: true },
	);
};

if (document.readyState !== "loading") {
	bootstrap();
} else {
	document.addEventListener("DOMContentLoaded", bootstrap, { once: true });
}
